// =============================================================================
// VandieZ admin-web -- Bid-review call log (/bid-calls)
// =============================================================================
// List of every Exotel call attempt the shipper made on a bid (Spec 6
// §4). Filter by status; click a row for the detail + recording URL.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  PhoneCall,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { bidCallsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminBidCallRow } from "@/types/bid_calls";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Completed", value: "completed" },
  { label: "In progress", value: "in_progress" },
  { label: "Initiated", value: "initiated" },
  { label: "Failed", value: "failed" },
];

const STATUS_PILL: Record<string, string> = {
  completed: "bg-success-subtle text-success-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  initiated: "bg-muted text-muted-foreground",
  failed: "bg-danger-subtle text-danger-subtle-foreground",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {return "—";}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatDuration(sec: number | null): string {
  if (sec === null) {return "—";}
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function BidCallsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  // URL holds the filter + offset so the operator can bookmark or share.
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<AdminBidCallRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  const statusFilter = searchParams.get("status") ?? "";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;
  const sizeRaw = Number.parseInt(searchParams.get("size") ?? "", 10);
  const pageSize = PAGE_SIZE_OPTIONS.includes(
    sizeRaw as (typeof PAGE_SIZE_OPTIONS)[number],
  )
    ? sizeRaw
    : DEFAULT_PAGE_SIZE;

  const updateFilters = useCallback(
    (next: { status?: string; offset?: number; size?: number }): void => {
      setSearchParams(
        (cur) => {
          const params = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (
              v === undefined ||
              v === "" ||
              v === 0 ||
              (k === "size" && v === DEFAULT_PAGE_SIZE)
            ) {
              params.delete(k);
            } else {
              params.set(k, String(v));
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await bidCallsAdminApi.list(
          accessToken,
          {
            limit: pageSize,
            offset,
            ...(statusFilter ? { status: statusFilter } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        if (e instanceof ApiError) {setError(e.problem.detail);}
        else {setError("Unexpected error. Try again.");}
      } finally {
        setLoading(false);
      }
    },
    [accessToken, statusFilter, offset, pageSize],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchPage(c.signal);
    return () => { c.abort(); };
  }, [fetchPage]);

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const res = await bidCallsAdminApi.list(accessToken, {
        limit: 200,
        offset: 0,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      const header = [
        "started_at",
        "shipper_name",
        "shipper_phone",
        "driver_name",
        "driver_phone",
        "lane",
        "bridge_kind",
        "duration_seconds",
        "status",
      ];
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [
        header.join(","),
        ...res.items.map((r) =>
          [
            r.started_at ?? "",
            r.shipper_name ?? "",
            r.shipper_phone ?? "",
            r.driver_name ?? "",
            r.driver_phone ?? "",
            r.lane ?? "",
            r.bridge_kind,
            r.duration_seconds ?? "",
            r.status,
          ]
            .map((c) => escape(String(c)))
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-bid-calls-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bid-review calls"
        description="Every Exotel call the shipper made to review a bid (Spec 6 §4). Click a row for the recording URL + full timing."
        actions={
          <Button
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={exporting || items.length === 0}
          >
            <Download className="mr-1 h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" /> Call log
          </CardTitle>
          <CardDescription>
            {total > 0
              ? `Showing ${offset + 1}–${offset + items.length} of ${total}.`
              : "No calls match the current filter."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex gap-1 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                type="button"
                onClick={() => { updateFilters({ status: f.value, offset: 0 }); }}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  statusFilter === f.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && items.length === 0 ? (
            <TableSkeleton columns={7} />
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No calls match.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Started</th>
                    <th className="py-2 pr-4">Shipper</th>
                    <th className="py-2 pr-4">Driver</th>
                    <th className="py-2 pr-4">Lane</th>
                    <th className="py-2 pr-4">Bridge</th>
                    <th className="py-2 pr-4">Duration</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-b-0 cursor-pointer hover:bg-muted/40 align-top"
                      onClick={() => { navigate(`/bid-calls/${row.id}`); }}
                    >
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {formatDateTime(row.started_at)}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium">
                          {row.shipper_name ?? "—"}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {row.shipper_phone ?? row.shipper_id.slice(0, 8)}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium">
                          {row.driver_name ?? "—"}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {row.driver_phone ?? row.driver_id.slice(0, 8)}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {row.lane ?? "—"}
                      </td>
                      <td className="py-3 pr-4 text-xs capitalize">
                        {row.bridge_kind.replace(/_/g, " ")}
                      </td>
                      <td className="py-3 pr-4 text-xs tabular-nums">
                        {formatDuration(row.duration_seconds)}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                            STATUS_PILL[row.status] ??
                              "bg-muted text-muted-foreground",
                          )}
                        >
                          {row.status.replace(/_/g, " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || loading}
              onClick={() => { updateFilters({ offset: Math.max(0, offset - pageSize) }); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>
                {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
              </span>
              <label className="flex items-center gap-1">
                Rows:
                <select
                  value={pageSize}
                  onChange={(e) => {
                    updateFilters({
                      size: Number.parseInt(e.target.value, 10),
                      offset: 0,
                    });
                  }}
                  className="rounded border bg-background px-1 py-0.5 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || loading}
              onClick={() => { updateFilters({ offset: offset + pageSize }); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
